import { useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import type { ConfluenceEdition } from '@confluence/session-data';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';

import { useReverseTrialLozengeEligible } from './useReverseTrialLozengeEligible';

interface ReverseTrialLozengeExperimentProps {
	cohort: ExperimentCohort;
	experimentName: string;
	experimentExposure: () => void;
	isGateEnabled: boolean;
	isUserInAudience: boolean;
	touchpointId: string;
	edition: ConfluenceEdition;
	shouldFireExposure?: boolean;
	loading: boolean;
	error?: string | null;
}

export enum ExperimentCohort {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	TREATMENT = 'treatment',
}

const useReverseTrialLozengeExperiment = ({
	cohort,
	experimentName,
	experimentExposure,
	isGateEnabled,
	isUserInAudience,
	touchpointId,
	edition,
	shouldFireExposure = true,
	loading,
	error,
}: ReverseTrialLozengeExperimentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEligible = isGateEnabled && cohort !== ExperimentCohort.NOT_ENROLLED && isUserInAudience;

	return useMemo(() => {
		if (!isEligible) {
			return {
				cohort,
				renderTreatment: false,
				loading,
				error,
			};
		}

		if (shouldFireExposure) {
			experimentExposure();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: `${experimentName}-exposed`,
					attributes: {
						cohort,
						touchpointId,
						edition,
					},
					source: experimentName,
				},
			}).fire();
		}

		return {
			cohort,
			renderTreatment: isEligible && cohort === ExperimentCohort.TREATMENT,
			loading,
			error,
		};
	}, [
		isEligible,
		createAnalyticsEvent,
		experimentName,
		touchpointId,
		cohort,
		experimentExposure,
		edition,
		shouldFireExposure,
		loading,
		error,
	]);
};

export const useReverseTrialLozengeExperimentAb = ({
	touchpointId,
	edition,
	shouldFireExposure,
}: {
	touchpointId: string;
	edition: ConfluenceEdition;
	shouldFireExposure?: boolean;
}) => {
	const experimentName = 'confluence_reverse_trial_lozenges';
	const isGateEnabled = Boolean(fg('confluence_reverse_trial_lozenges_fg'));

	const {
		isEligible,
		loading: isAbEligibleLoading,
		error: hasAbEligibleError,
	} = useReverseTrialLozengeEligible({
		experimentName,
		isGateEnabled,
	});

	const [experimentConfig, experimentExposure] = UNSAFE_noExposureExp(
		'confluence_reverse_trial_lozenges',
	);
	const cohort = experimentConfig.get('cohort', ExperimentCohort.NOT_ENROLLED) as ExperimentCohort;

	return useReverseTrialLozengeExperiment({
		cohort,
		experimentName,
		experimentExposure,
		shouldFireExposure,
		isGateEnabled,
		isUserInAudience: isEligible,
		touchpointId,
		edition,
		loading: isAbEligibleLoading,
		error: hasAbEligibleError,
	});
};
